import React, { memo, useMemo, useRef, useState } from 'react';

import { Canvas } from '@react-three/fiber';
import { SimpleNaruto } from './SimpleNaruto';
import { SimpleSakura } from './SimpleSakura';
import { SimpleSasuke } from './SimpleSasuke';

interface CharacterSelectModalProps {
  onSelect: (character: string) => void;
}

// Memoize the character components to prevent unnecessary re-renders
const MemoizedNaruto = memo(SimpleNaruto);
const MemoizedSasuke = memo(SimpleSasuke);
const MemoizedSakura = memo(SimpleSakura);

export const CharacterSelectModal: React.FC<CharacterSelectModalProps> = ({
  onSelect,
}) => {
  const [selected, setSelected] = useState<'naruto' | 'sasuke' | 'sakura'>(
    'naruto'
  );

  // Small preview scene for each character
  const CharacterPreview = memo(
    ({ character }: { character: 'naruto' | 'sasuke' | 'sakura' }) => {
      // Use refs to prevent unnecessary re-renders
      const canvasRef = useRef<HTMLCanvasElement>(null);

      // Fixed props to stabilize rendering
      const fixedProps = useMemo(
        () => ({
          position: [0, 1, 0] as [number, number, number],
          isControlled: false,
          health: 100,
          isDead: false,
          facingDirection: [0, 0, 1] as [number, number, number],
        }),
        []
      );

      return (
        <div
          style={{
            width: '100%',
            height: '150px',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '8px',
          }}
        >
          <Canvas
            shadows
            frameloop="demand"
            camera={{ position: [0, 1.5, 3], fov: 40 }}
            style={{ background: 'transparent' }}
            ref={canvasRef}
            gl={{ preserveDrawingBuffer: true }}
            dpr={[1, 1.5]} // Limit pixel ratio for better performance
          >
            <ambientLight intensity={0.6} />
            <directionalLight position={[2, 2, 1]} intensity={1} castShadow />
            {character === 'naruto' ? (
              <MemoizedNaruto
                key="naruto-preview"
                position={fixedProps.position}
                isControlled={fixedProps.isControlled}
                health={fixedProps.health}
                isDead={fixedProps.isDead}
                facingDirection={fixedProps.facingDirection}
              />
            ) : character === 'sasuke' ? (
              <MemoizedSasuke
                key="sasuke-preview"
                position={fixedProps.position}
                isControlled={fixedProps.isControlled}
                health={fixedProps.health}
                isDead={fixedProps.isDead}
                facingDirection={fixedProps.facingDirection}
              />
            ) : (
              <MemoizedSakura
                key="sakura-preview"
                position={fixedProps.position}
                isControlled={fixedProps.isControlled}
                health={fixedProps.health}
                isDead={fixedProps.isDead}
                facingDirection={fixedProps.facingDirection}
              />
            )}
          </Canvas>
        </div>
      );
    }
  );

  // Memoize card elements to prevent unnecessary re-renders
  const narutoCard = useMemo(
    () => (
      <div
        style={{
          flex: 1,
          border: '2px solid',
          borderColor: selected === 'naruto' ? '#ff6600' : '#262640',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          boxShadow: selected === 'naruto' ? '0 0 20px #ff6600' : 'none',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
        }}
        onClick={() => setSelected('naruto')}
      >
        <CharacterPreview character="naruto" />
      </div>
    ),
    [selected]
  );

  const sasukeCard = useMemo(
    () => (
      <div
        style={{
          flex: 1,
          border: '2px solid',
          borderColor: selected === 'sasuke' ? '#0D47A1' : '#262640',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          boxShadow: selected === 'sasuke' ? '0 0 20px #0D47A1' : 'none',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
        }}
        onClick={() => setSelected('sasuke')}
      >
        <CharacterPreview character="sasuke" />
      </div>
    ),
    [selected]
  );

  // Add Sakura card
  const sakuraCard = useMemo(
    () => (
      <div
        style={{
          flex: 1,
          border: '2px solid',
          borderColor: selected === 'sakura' ? '#E91E63' : '#262640',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          boxShadow: selected === 'sakura' ? '0 0 20px #E91E63' : 'none',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
        }}
        onClick={() => setSelected('sakura')}
      >
        <CharacterPreview character="sakura" />
      </div>
    ),
    [selected]
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      }}
    >
      <div
        style={{
          background: 'rgba(26, 26, 46, 0.6)',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '800px', // Increased width for three characters
          width: '90%',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <h1
          style={{
            fontSize: '2rem',
            marginBottom: '1.5rem',
            textAlign: 'center',
            color: 'white',
          }}
        >
          Select Your Character
        </h1>

        <div
          style={{
            display: 'flex',
            gap: '1.5rem',
            marginBottom: '2rem',
            flexDirection: window.innerWidth < 768 ? 'column' : 'row', // Adjust breakpoint for three cards
          }}
        >
          {narutoCard}
          {sasukeCard}
          {sakuraCard}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            style={{
              backgroundColor:
                selected === 'naruto'
                  ? '#FF6600'
                  : selected === 'sasuke'
                  ? '#0D47A1'
                  : '#E91E63', // Pink for Sakura
              color: 'white',
              border: 'none',
              padding: '0.8rem 2rem',
              fontSize: '1.2rem',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.2s ease',
            }}
            onClick={() => onSelect(selected)}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
            }}
          >
            Start Game
          </button>
        </div>
      </div>
    </div>
  );
};
